'use client';

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Eye, EyeOff, ChevronDown, ChevronRight } from 'lucide-react';
import { DropdownMenuItem, DropdownMenuRadioGroup, DropdownMenuRadioItem } from '@/components/ui/dropdown-menu';
import {
  VISIBILITY_REGISTRY,
  REGISTRY_SIZE,
  VISIBILITY_PRESETS,
  PRESET_META,
  DEFAULT_PREFERENCES,
  IMPORTANCE_LABELS,
  TECH_LEVEL_LABELS,
  INVESTIGATIVE_LABELS,
} from '@/lib/visibility';
import type { VisibilityPresetName, VisibilityPreferences } from '@/lib/visibility';

// ── Storage (same key as the context provider) ──────────────────────────────

const STORAGE_KEY = 'pref:call_layout_visibility_v2';

function readPrefs(): VisibilityPreferences {
  if (typeof window === 'undefined') return DEFAULT_PREFERENCES;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFERENCES;
    const parsed = JSON.parse(raw);
    return {
      activePreset: parsed.activePreset ?? DEFAULT_PREFERENCES.activePreset,
      importanceThreshold: parsed.importanceThreshold ?? DEFAULT_PREFERENCES.importanceThreshold,
      techLevelThreshold: parsed.techLevelThreshold ?? DEFAULT_PREFERENCES.techLevelThreshold,
      investigativeThreshold: parsed.investigativeThreshold ?? DEFAULT_PREFERENCES.investigativeThreshold,
      overrides: parsed.overrides ?? {},
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

function writePrefs(prefs: VisibilityPreferences) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    window.dispatchEvent(new StorageEvent('storage', {
      key: STORAGE_KEY,
      newValue: JSON.stringify(prefs),
    }));
  } catch { /* ignore */ }
}

function computeHiddenCount(prefs: VisibilityPreferences): number {
  let hidden = 0;
  for (const [id, entry] of Object.entries(VISIBILITY_REGISTRY)) {
    if (entry.critical) continue;
    const override = prefs.overrides[id];
    if (override === true) continue;
    if (override === false) { hidden++; continue; }
    const ok =
      entry.importance >= prefs.importanceThreshold &&
      entry.techLevel <= prefs.techLevelThreshold &&
      entry.investigativeValue >= prefs.investigativeThreshold;
    if (!ok) hidden++;
  }
  return hidden;
}

// ── Compact slider ──────────────────────────────────────────────────────────

function MiniSlider({
  label,
  value,
  max,
  labels,
  color,
  onChange,
}: {
  label: string;
  value: number;
  max: number;
  labels: Record<number, string>;
  color: string;
  onChange: (v: number) => void;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-white/70 uppercase tracking-wider">{label}</span>
        <span className={`text-[10px] font-semibold ${color}`}>{labels[value]}</span>
      </div>
      <input aria-label={label}
        type="range"
        min={0}
        max={max}
        step={1}
        value={value}
        onChange={(e) => { e.stopPropagation(); onChange(Number(e.target.value)); }}
        onClick={(e) => e.stopPropagation()}
        className="w-full h-1 rounded-full appearance-none cursor-pointer bg-slate-700
          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
          [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border
          [&::-webkit-slider-thumb]:border-blue-500 [&::-webkit-slider-thumb]:shadow [&::-webkit-slider-thumb]:cursor-pointer
          [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:rounded-full
          [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border [&::-moz-range-thumb]:border-blue-500
          [&::-moz-range-thumb]:cursor-pointer"
      />
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export function InlineVisibilityControls() {
  const [prefs, setPrefs] = useState<VisibilityPreferences>(readPrefs);
  const [expanded, setExpanded] = useState(false);

  // Track what we last wrote so the storage listener can skip self-triggered events.
  // Value comparison is more robust than timing-based flags (no microtask race window).
  const lastWrittenRef = useRef<string>('');

  useEffect(() => {
    const serialized = JSON.stringify(prefs);
    lastWrittenRef.current = serialized;
    writePrefs(prefs);
  }, [prefs]);

  // Listen for changes from settings page or other tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      // Skip if this is our own write (same serialized value)
      if (e.newValue === lastWrittenRef.current) return;
      try { setPrefs(JSON.parse(e.newValue)); } catch { /* ignore */ }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const hiddenCount = useMemo(() => computeHiddenCount(prefs), [prefs]);
  const visibleCount = REGISTRY_SIZE - hiddenCount;

  const applyPreset = useCallback((preset: VisibilityPresetName) => {
    const t = VISIBILITY_PRESETS[preset];
    if (!t) return;
    setPrefs({ activePreset: preset, ...t, overrides: {} });
  }, []);

  const updateThreshold = useCallback((key: keyof VisibilityPreferences, value: number) => {
    setPrefs(prev => ({ ...prev, [key]: value, activePreset: 'custom' as VisibilityPresetName }));
  }, []);

  const presetOrder: VisibilityPresetName[] = ['essentials', 'standard', 'advanced', 'full', 'training', 'developer'];

  return (
    <div className="border-t border-white/[0.06] py-1.5 px-1.5" onClick={(e) => e.stopPropagation()}>
      {/* Collapsed row — click to expand */}
      <DropdownMenuItem
        icon={hiddenCount > 0 ? EyeOff : Eye}
        iconTone="cat-2"
        iconTile
        aria-expanded={expanded}
        onSelect={(event) => { event.preventDefault(); setExpanded((value) => !value); }}
        description={
          <>
            {visibleCount}/{REGISTRY_SIZE} visible
            {hiddenCount > 0 && <span className="text-danger"> · {hiddenCount} hidden</span>}
          </>
        }
      >
        <span className="flex items-center justify-between gap-2">
          <span>
            Layout: {PRESET_META[prefs.activePreset]?.label ?? 'Custom'}
          </span>
          {expanded ? <ChevronDown className="icon shrink-0 text-ink-muted" aria-hidden="true" /> : <ChevronRight className="icon shrink-0 text-ink-muted" aria-hidden="true" />}
        </span>
      </DropdownMenuItem>

      {/* Expanded controls */}
      {expanded && (
        <div className="mt-1.5 mx-1 space-y-3 px-2 py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.06]">
          {/* Preset chips */}
          <DropdownMenuRadioGroup value={prefs.activePreset} className="flex flex-wrap gap-1">
            {presetOrder.map((p) => (
              <DropdownMenuRadioItem
                key={p}
                value={p}
                onSelect={(event) => { event.preventDefault(); applyPreset(p); }}
                className="w-auto"
              >
                {PRESET_META[p].label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>

        </div>
      )}
    </div>
  );
}
